import React from 'react';
import { X } from 'lucide-react';
import {
  BookingOfferConfig,
  serviceTypeLabel,
  tierSummary,
} from '../../../services/MasterAdmin/bookingOfferService';
import type { DeepLinkRoute } from '../../../models/MasterAdmin/deepLink';

type Props = {
  open: boolean;
  offer: BookingOfferConfig | null;
  deepLinkRoutes?: DeepLinkRoute[];
  onClose: () => void;
};

const routeLabel = (routeId: string | null | undefined, routes: DeepLinkRoute[]) => {
  if (!routeId) return 'Default for service type';
  const route = routes.find((r) => r.id === routeId);
  return route ? `${route.label} (${route.slug})` : routeId;
};

const displayModeLabel = (mode?: string | null) => {
  switch (mode) {
    case 'IMAGE_ONLY':
      return 'Image only';
    case 'TEXT_ONLY':
      return 'Text only';
    case 'BOTH':
      return 'Image + text';
    default:
      return 'Image + text';
  }
};

const DetailItem: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <p className="text-xs font-medium uppercase text-gray-500">{label}</p>
    <div className="mt-1 text-gray-900 dark:text-white">{children}</div>
  </div>
);

const BookingOfferViewModal: React.FC<Props> = ({ open, offer, deepLinkRoutes = [], onClose }) => {
  if (!open || !offer) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3">
      <div className="flex max-h-[94vh] w-full max-w-7xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-900">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Offer details — {serviceTypeLabel(offer.serviceType)}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5 text-sm">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <section className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Overview</h3>
              <div className="grid grid-cols-2 gap-4">
                <DetailItem label="Service">{serviceTypeLabel(offer.serviceType)}</DetailItem>
                <DetailItem label="Status">
                  <span
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      offer.isActive
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {offer.isActive ? 'Active' : 'Inactive'}
                  </span>
                </DetailItem>
              </div>
              <DetailItem label="Headline">{offer.marketingTitle || '—'}</DetailItem>
              <DetailItem label="Description">
                <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                  {offer.popupDescription || '—'}
                </p>
              </DetailItem>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <DetailItem label="Valid from">
                  {offer.validFrom ? new Date(offer.validFrom).toLocaleString() : '—'}
                </DetailItem>
                <DetailItem label="Valid until">
                  {offer.validUntil ? new Date(offer.validUntil).toLocaleString() : '—'}
                </DetailItem>
              </div>
              <p className="text-xs text-gray-500">Config ID: {offer.offerConfigId}</p>
            </section>

            <section className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Home popup
              </h3>
              <DetailItem label="Display mode">
                {displayModeLabel(offer.popupDisplayMode)}
              </DetailItem>
              <DetailItem label="Tap navigates to">
                {routeLabel(offer.ctaDeepLinkRouteId, deepLinkRoutes)}
              </DetailItem>
              {offer.popupImageUrl ? (
                <DetailItem label="Banner image">
                  <img
                    src={offer.popupImageUrl}
                    alt="Offer banner"
                    className="h-24 shrink-0 rounded-lg border border-gray-200 object-cover dark:border-gray-700"
                    style={{ aspectRatio: '352/170' }}
                  />
                </DetailItem>
              ) : (
                <DetailItem label="Banner image">—</DetailItem>
              )}
            </section>

            <section className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Splash screen
              </h3>
              <DetailItem label="Status">
                {offer.showOnSplash ? (
                  <span className="inline-flex rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold text-sky-800 dark:bg-sky-900/40 dark:text-sky-300">
                    Enabled
                  </span>
                ) : (
                  'Disabled'
                )}
              </DetailItem>
              {offer.showOnSplash ? (
                <>
                  <DetailItem label="Display mode">
                    {displayModeLabel(offer.splashDisplayMode)}
                  </DetailItem>
                  <DetailItem label="Background">
                    <div className="flex items-center gap-2">
                      {offer.splashBackgroundColor ? (
                        <span
                          className="inline-block h-5 w-5 rounded border border-gray-300"
                          style={{ backgroundColor: offer.splashBackgroundColor }}
                        />
                      ) : null}
                      {offer.splashBackgroundColor || 'Default app color'}
                    </div>
                  </DetailItem>
                  <DetailItem label="Splash image">
                    {offer.splashImageUrl ? (
                      <img
                        src={offer.splashImageUrl}
                        alt="Splash banner"
                        className="h-24 shrink-0 rounded-lg border border-gray-200 object-cover dark:border-gray-700"
                        style={{ aspectRatio: '352/170' }}
                      />
                    ) : (
                      <span className="text-gray-500">Uses popup banner image</span>
                    )}
                  </DetailItem>
                </>
              ) : null}
            </section>
          </div>

          <section className="mt-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
              Discount tiers
            </h3>
            <p className="mb-3 text-gray-700 dark:text-gray-300">{tierSummary(offer.tiers)}</p>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              {(offer.tiers || []).map((tier) => (
                <div
                  key={tier.bookingNumber}
                  className="rounded-md border border-gray-200 p-3 dark:border-gray-700"
                >
                  <p className="font-medium text-gray-900 dark:text-white">
                    Booking #{tier.bookingNumber} — {tier.discountPercent}% off
                  </p>
                  {tier.popupImageUrl ? (
                    <img
                      src={tier.popupImageUrl}
                      alt={`Tier ${tier.bookingNumber} banner`}
                      className="mt-2 h-20 shrink-0 rounded object-cover"
                      style={{ aspectRatio: '352/170' }}
                    />
                  ) : (
                    <p className="mt-2 text-xs text-gray-500">No tier banner — uses default</p>
                  )}
                  {tier.title ? (
                    <p className="mt-2 text-gray-700 dark:text-gray-300">{tier.title}</p>
                  ) : null}
                  {tier.description ? (
                    <p className="text-gray-600 dark:text-gray-400">{tier.description}</p>
                  ) : null}
                  <p className="mt-1 text-xs text-gray-500">
                    Navigate: {routeLabel(tier.ctaDeepLinkRouteId, deepLinkRoutes)}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default BookingOfferViewModal;
